import { SET_ASIDE_COUNT, SET_ASIDE_QUIET } from "../core/ops";
import type { Task } from "../core/schema";

/** The third time in a row a task is set aside, Drang asks once (`UI_ANALYSIS.md` §4.1a). */
export const ASK_AFTER = 3;
/** "Keep as is" silences the question for this long. */
export const QUIET_DAYS = 30;

/** The return points offered: tomorrow at 08:00, this evening at 18:00 (while it is earlier), next Monday at 08:00. */
export function returnPoints(now: Date): { key: "tomorrow" | "evening" | "nextWeek"; at: Date }[] {
  const at = (days: number, hour: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(hour, 0, 0, 0);
    return d;
  };
  const out: { key: "tomorrow" | "evening" | "nextWeek"; at: Date }[] = [];
  if (now.getHours() < 17) out.push({ key: "evening", at: at(0, 18) });
  out.push({ key: "tomorrow", at: at(1, 8) });
  out.push({ key: "nextWeek", at: at((8 - now.getDay()) % 7 || 7, 8) });
  return out;
}

/** Whether setting the task aside now should ask the question (the count it reached, and not silenced). */
export function shouldAsk(task: Task, count: number, nowMs: number): boolean {
  const quiet = task.props[SET_ASIDE_QUIET];
  return count >= ASK_AFTER && !(typeof quiet === "string" && Date.parse(quiet) > nowMs);
}

/** The weekly look at what is set aside (UI cleanup U4); someday tasks are in it less often. */
export const REVIEW_EVERY_DAYS = 7;
export const SOMEDAY_EVERY_DAYS = 28;
const REVIEW_KEYS = { aside: "drang.device.asideReviewAt", someday: "drang.device.somedayReviewAt" } as const;

/** When this device last looked (ms), or null: per device, like the other `drang.device.*` keys. */
export function readReviewAt(kind: keyof typeof REVIEW_KEYS): number | null {
  try {
    const v = localStorage.getItem(REVIEW_KEYS[kind]);
    return v && Number.isFinite(Number(v)) ? Number(v) : null;
  } catch {
    return null;
  }
}

export function writeReviewAt(kind: keyof typeof REVIEW_KEYS, at: number): void {
  try {
    localStorage.setItem(REVIEW_KEYS[kind], String(at));
  } catch {
    /* no storage: it asks again next time */
  }
}

export const reviewDue = (last: number | null, days: number, nowMs: number) =>
  last === null || nowMs - last >= days * 86_400_000;

/**
 * What the weekly look offers: tasks set aside with *Not now* that are still away (soonest back first), and, once
 * every four weeks, the someday tasks. Nothing when no look is due.
 */
export function reviewCandidates(
  tasks: Task[],
  nowMs: number,
  last: { aside: number | null; someday: number | null },
  notBefore: (task: Task, nowMs: number) => number | null,
): { aside: { task: Task; back: number }[]; someday: Task[] } {
  const open = tasks.filter((t) => !t.doneAt && !t.archivedAt && t.typeId !== "note");
  const aside = reviewDue(last.aside, REVIEW_EVERY_DAYS, nowMs)
    ? open
        .filter((t) => typeof t.props[SET_ASIDE_COUNT] === "number")
        .map((t) => ({ task: t, back: notBefore(t, nowMs) }))
        .filter((r): r is { task: Task; back: number } => r.back !== null && r.back > nowMs)
        .sort((a, b) => a.back - b.back)
    : [];
  const away = new Set(aside.map((r) => r.task.id));
  const someday = reviewDue(last.someday, SOMEDAY_EVERY_DAYS, nowMs)
    ? open
        .filter((t) => t.props.horizon === "someday" && !away.has(t.id))
        .sort((a, b) => a.title.localeCompare(b.title))
    : [];
  return { aside, someday };
}
